// Salary payment status helpers — Payroll Approval's Payment column and the
// bank-statement upload dialog (components/BankStatementUploadDialog.tsx).

export type PaymentBucket = "paid" | "returned" | "unpaid";

export const paymentBucketFor = (record: {
  payment?: { status?: string | null } | null;
}): PaymentBucket => {
  const status = (record.payment?.status || "").trim().toUpperCase();
  if (status === "PAID") return "paid";
  if (status === "RETURNED") return "returned";
  return "unpaid";
};

type StatementPickRow = {
  salary_id: string;
  result: string;
  candidates: { key: string }[];
  returned?: { key: string } | null;
};

// Which bank line each salary is recorded with when a statement is applied.
// Matched rows start on their debit and returned rows on the bounce; rows that
// need review start empty so a person has to choose.
export const initialStatementPicks = (rows: StatementPickRow[]): Record<string, string> => {
  const picks: Record<string, string> = {};
  rows.forEach((row) => {
    if (row.result === "matched" && row.candidates[0]) picks[row.salary_id] = row.candidates[0].key;
    if (row.result === "returned" && row.returned) picks[row.salary_id] = row.returned.key;
  });
  return picks;
};

export const statementSelections = (picks: Record<string, string>) =>
  Object.entries(picks)
    .filter(([, key]) => Boolean(key))
    .map(([salary_id, key]) => ({ salary_id, key }));

const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-08-08" → "08 Aug 2026", read straight off the string: `new Date()`
// takes a bare date as UTC midnight and can show the previous day.
export const formatIsoDate = (iso?: string | null) => {
  if (!iso) return "—";
  const [year, month, day] = iso.slice(0, 10).split("-");
  const label = SHORT_MONTHS[Number(month) - 1];
  return label && day ? `${day} ${label} ${year}` : iso;
};

// Today as YYYY-MM-DD in the viewer's timezone (toISOString would use UTC).
export const localIsoDate = (d: Date = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
